import type { ServerMessage } from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { CenterScreen } from "@screener/uikit/components/center-screen";
import { useTheme } from "@screener/uikit/hooks/use-theme";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAlertActions } from "@/alerts/use-alert-actions";
import { AppShell } from "@/app-shell";
import { AuthLayout, AuthStatus } from "@/auth/auth-layout";
import { ConfirmEmailPage } from "@/auth/confirm-email-page";
import { PasswordResetPage } from "@/auth/password-reset-page";
import { PasswordResetRequestScreen } from "@/auth/password-reset-request-screen";
import { streamExchanges } from "@/density/stream-exchanges";
import { useDensityStream } from "@/density/use-density-stream";
import { sameStringList } from "@/density/use-stable";
import { useStreamSelector } from "@/density/use-stream-selector";
import { useConfirmRoute } from "@/hooks/use-confirm-route";
import { useIpcConnection } from "@/hooks/use-ipc-connection";
import { useForgotRoute, useResetRoute } from "@/hooks/use-reset-route";
import { hashOfDensityView, useDensityView, useScreen } from "@/hooks/use-screen";
import { useSession } from "@/hooks/use-session";
import { useSignupOptions } from "@/hooks/use-signup-options";
import { useSignupRoute } from "@/hooks/use-signup-route";
import { capturePageview, identifyUser, pageviewScreenName, resetUser } from "@/lib/analytics";
import { logout } from "@/lib/auth-api";
import { createDeletionGuard } from "@/lib/deletion-guard";
import { signupGate } from "@/lib/signup-gate";
import {
  clearConfirmToken,
  clearResetToken,
  clearSignupToken,
  confirmTokenToSubmit,
  FORGOT_HASH,
  forgotRouteIsStale,
  forgotRouteShown,
  leaveSignupRoute,
  loginNoticeForSession,
  sessionNeedsRecheck,
  signupRouteIsStale,
} from "@/lib/signup-route";
import { LoginForm } from "@/login-form";
import { AccountSettingsProvider } from "@/settings/account-settings-provider";
import { SignupForm } from "@/signup-form";
import { TakenOverScreen } from "@/taken-over-screen";

/**
 * Каркас клиента: сессия, поток и экран живой таблицы §9.
 *
 * Границы: экраны собираются из компонентов @screener/uikit; типы сообщений —
 * только из @screener/protocol. Доменной логики здесь нет — она в демоне.
 */
export function App() {
  // Тема висит на каркасе, а не на экране: класс `dark` вешается на корень
  // документа, и вешать его обязан тот, кто живёт всю сессию, — иначе форма
  // входа и таблица расходились бы темой.
  const theme = useTheme();
  const screen = useScreen();
  const { session, markAuthenticated, markAnonymous, retryCheck, rereadMe } = useSession();
  const authenticated = session.status === "authenticated";
  const sessionLogin = session.status === "authenticated" ? session.login : null;
  const sessionStatus = session.status;
  const densityView = useDensityView();
  // Маршрут регистрации разбирается до проверки сессии (stage9-signup-invites,
  // Д9): токен ссылки уже убран из адреса, здесь лишь читается из памяти.
  const signupRoute = useSignupRoute();
  // Флаги регистрации у демона нужны и форме входа (ссылка), и странице
  // регистрации, и меню (пункт «Пригласить»); сессия `checking`/`unavailable`
  // их не просит.
  const signupFlags = useSignupOptions(
    session.status === "anonymous" || session.status === "authenticated",
    session.status,
  );

  // Вошедший не должен видеть форму регистрации: ссылку вставили в открытое
  // приложение, а после «Выйти» открылась бы форма со старым токеном.
  const signupRouteActive = signupRoute.active;
  useEffect(() => {
    if (signupRouteIsStale(sessionStatus === "authenticated", signupRouteActive)) {
      clearSignupToken();
      leaveSignupRoute();
    }
  }, [sessionStatus, signupRouteActive]);

  // Ссылка подтверждения почты, в отличие от регистрации, обрабатывается при
  // любой сессии: письмо открывают в браузере, где уже кто-то вошёл, и токен
  // нельзя ни выбросить, ни оставить непотраченным. Адрес `#/confirm` уходит
  // вместе со страницей после ответа демона.
  const confirmRoute = useConfirmRoute();
  const confirmToken = confirmTokenToSubmit(confirmRoute.active, confirmRoute.token);

  // Ссылка сброса пароля (stage9-password-reset, Р6) — по тем же причинам при
  // любой сессии: токен нельзя ни выбросить, ни оставить непотраченным. Экран
  // запроса (`#/forgot`) тоже при любой сессии: на него ведёт «Запросить новую
  // ссылку» с недействительной ссылки, а её открывает и вошедший.
  const resetRoute = useResetRoute();
  const resetToken = resetRoute.active ? resetRoute.token : null;
  const resetPageShown = resetToken !== null;
  const forgotRoute = useForgotRoute();

  // На `#/forgot` нет `AppShell`, «Выйти» там нет: из `authenticated` в
  // `anonymous` на этом экране переводит только погасшая сессия (4401, выход в
  // другой вкладке). Хвост `#/forgot` тогда убирается: человек ждёт форму входа.
  // Вышедший, сам открывший `#/forgot`, перехода не даёт и остаётся на экране.
  // Экран и просмотр аналитики считаются в рендере по тому же признаку
  // (`forgotShown`), не дожидаясь эффекта, — иначе уйдёт просмотр экрана,
  // который уже не показан.
  const previousStatus = useRef(sessionStatus);
  const forgotShown = forgotRouteShown(previousStatus.current, sessionStatus, forgotRoute);
  useEffect(() => {
    if (forgotRouteIsStale(previousStatus.current, sessionStatus, forgotRoute)) {
      leaveSignupRoute();
    }
    previousStatus.current = sessionStatus;
  }, [sessionStatus, forgotRoute]);

  // Идентичность в аналитике следует за состоянием сессии: так покрыты вход,
  // найденная при запуске сессия, выход и истечение (§103 п. 2, design.md Д5).
  // `checking` и `unavailable` её не трогают: сбой связи с демоном — не выход.
  useEffect(() => {
    if (sessionStatus === "authenticated" && sessionLogin !== null) {
      identifyUser(sessionLogin);
    } else if (sessionStatus === "anonymous") {
      resetUser();
    }
  }, [sessionStatus, sessionLogin]);

  // Просмотр экрана — по смене маршрута из `use-screen`, а не слушателем
  // hashchange в модуле аналитики (design.md Д6). Маршрут — экран и вид; ещё
  // статус сессии: форма входа и заглушки рисуются при любом адресе. Объявлен
  // после эффекта идентичности — просмотр формы входа идёт после `resetUser()`.
  useEffect(() => {
    // Вид в адресе называется `table`, а не `densities` (use-screen).
    const viewName = densityView === null ? null : hashOfDensityView(densityView).slice(2);
    // Экраны сброса пароля называются отдельно; рисуются они, только если
    // есть токен (`#/reset`) либо сессия не в заглушке (`#/forgot`, см. имя).
    const authRoute = resetPageShown ? "reset" : forgotShown ? "forgot" : null;
    capturePageview(pageviewScreenName(sessionStatus, screen, viewName, authRoute));
  }, [sessionStatus, screen, densityView, resetPageShown, forgotShown]);
  const [loginNotice, setLoginNotice] = useState<string | null>(null);

  // Сообщение формы входа не переживает действующую сессию: подтверждение почты
  // сессию не меняет, а после смены пароля другой записи перечтение вернуло
  // сессию, и «Почта подтверждена, войдите» или «Пароль изменён, войдите» не
  // должны всплыть после следующего выхода.
  useEffect(() => {
    setLoginNotice((notice) => loginNoticeForSession(sessionStatus, notice));
  }, [sessionStatus]);

  // Состояние потока лежит во внешнем хранилище: корень его в рендере не
  // читает и кадрами потока не перерисовывается.
  const streamStore = useDensityStream();
  const { onMessage, reset: resetStream } = streamStore;

  const [deletionGuard] = useState(createDeletionGuard);

  const handleSessionInvalid = useCallback(() => {
    // Уводит на форму молча иначе: человек не отличит «сессия истекла» от
    // «я нажал Выйти» (design review stage5-auth-sessions, пункт 7).
    // После намеренного удаления учётной записи 4401 приходит раньше `204` и
    // уведомления не ставит (`deletion-guard`).
    setLoginNotice(deletionGuard.noticeForSessionInvalid());
    // Плотности принадлежат сессии: за формой входа им не место, и вошедший
    // следом не должен увидеть чужой состав вперемешку со своим.
    resetStream();
    markAnonymous();
  }, [markAnonymous, resetStream, deletionGuard]);

  const alerts = useAlertActions({
    enabled: authenticated,
    onSessionInvalid: handleSessionInvalid,
  });

  // Биржи для выбора в форме правила алерта (design.md stage6-alert-web
  // решение 4, spec `web-alerts-screen`) и в фильтрах библиотеки — уникальные
  // биржи инструментов потока, а не список, зашитый в клиент.
  const alertExchanges = useStreamSelector(streamStore, streamExchanges, sameStringList);

  // Одно сообщение потока §7.6 идёт двум потребителям: таблице и звуку/
  // нотификации §8 (1.20). `useIpcConnection` отдаёт его одному колбэку —
  // развилка ставится здесь (design.md «Где живёт код»).
  const handleStreamMessage = useCallback(
    (message: ServerMessage) => {
      onMessage(message);
      // `onMessage` только буферизует, так что снимок — состояние до применения
      // буфера, то же, что отдавал рендер. База в написании биржи по
      // инструментам: срабатывание алерта её не несёт, а нотификация пишет
      // инструмент тикером (§63) — индекс копится в состоянии потока.
      const { wrapped_coins, exchange_bases } = streamStore.getSnapshot();
      alerts.onMessage(message, wrapped_coins, exchange_bases);
    },
    [onMessage, alerts.onMessage, streamStore],
  );

  const {
    status: connectionStatus,
    statusSinceMs,
    openHere,
  } = useIpcConnection({
    enabled: authenticated,
    onSessionInvalid: handleSessionInvalid,
    // Доступ изменился при открытом потоке: сокет уже переоткрывается, а тариф
    // и состав замков перечитываются из `me`.
    onAccessChanged: rereadMe,
    onMessage: handleStreamMessage,
  });

  const handleLogout = useCallback(() => {
    logout().then((result) => {
      // Отказ демона и сетевая ошибка тоже ведут на форму (web-login,
      // «Демон не ответил на выход») — остаться внутри при попытке выйти
      // хуже, чем предупредить, что сессия могла остаться действительной.
      setLoginNotice(
        result === "success"
          ? null
          : "Выход не подтверждён демоном — сессия могла остаться действительной там.",
      );
      resetStream();
      markAnonymous();
    });
  }, [markAnonymous, resetStream]);

  // Учётная запись удалена (`204`, §130): тот же путь на форму входа, что у
  // «Выйти», — поток плотностей сброшен, сессия анонимна, перезагрузки нет.
  // Предупреждения не нужно: демон сам погасил куку и закрыл потоки.
  // Поздний `204` попытки, после которой человек уже вошёл заново, не
  // применяется (номер попытки в `deletion-guard`).
  const handleAccountDeleted = useCallback(
    (attempt: number) => {
      if (!deletionGuard.settleDeleted(attempt)) {
        return;
      }
      setLoginNotice(null);
      resetStream();
      markAnonymous();
    },
    [markAnonymous, resetStream, deletionGuard],
  );

  const handleLoginSuccess = useCallback(
    (login: string) => {
      setLoginNotice(null);
      deletionGuard.reset();
      markAuthenticated(login);
    },
    [markAuthenticated, deletionGuard],
  );

  // Регистрация сессии не выдаёт (stage9-email-confirmation, Р6): с экрана
  // «Подтвердите почту» человек возвращается ко входу.
  const handleSignupBack = useCallback(() => {
    clearSignupToken();
    leaveSignupRoute();
  }, []);

  // Подтверждение почты сессию не меняет (`204` без cookie): перечитывать нечего
  // при любом статусе. Анонимному — форма входа с сообщением; у вошедшего
  // сообщение гасит эффект `loginNoticeForSession`.
  const handleEmailConfirmed = useCallback(() => {
    clearConfirmToken();
    leaveSignupRoute();
    setLoginNotice("Почта подтверждена, войдите");
  }, []);

  // «Войти» на экране недействительной ссылки: токен потрачен, форма входа.
  const handleConfirmSignIn = useCallback(() => {
    clearConfirmToken();
    leaveSignupRoute();
  }, []);

  // Пароль сменён (`204`): сессии учётной записи демон отозвал, человек входит
  // заново. Кука не выдана, поэтому сессия перечитывается, а форма входа
  // получает сообщение.
  const handlePasswordChanged = useCallback(() => {
    clearResetToken();
    leaveSignupRoute();
    setLoginNotice("Пароль изменён, войдите");
    if (sessionStatus === "authenticated") {
      resetStream();
    }
    // `unavailable` тоже перечитывается: иначе человек увидел бы «Демон недоступен».
    if (sessionNeedsRecheck(sessionStatus)) {
      retryCheck();
    }
  }, [sessionStatus, retryCheck, resetStream]);

  // «Вернуться» с экрана запроса сброса: вошедшему — в приложение, вышедшему —
  // на форму входа; адрес в обоих случаях `#/`.
  const forgotBackLabel = authenticated ? "Вернуться в приложение" : undefined;

  // Недействительная ссылка: токен потрачен, дальше — запрос новой.
  const handleResetRequestNew = useCallback(() => {
    clearResetToken();
    window.location.hash = FORGOT_HASH;
  }, []);

  // Один индикатор на проверку сессии и чтение настроек аккаунта (§127 п. 4):
  // человек видит один «загрузочный» экран, а не два разных.
  const checkingScreen = (
    <CenterScreen aria-busy="true">
      <p className="text-sm text-muted-foreground">Проверяем сессию…</p>
    </CenterScreen>
  );

  if (confirmToken !== null) {
    return (
      <ConfirmEmailPage
        key={confirmToken}
        token={confirmToken}
        onConfirmed={handleEmailConfirmed}
        onSignIn={handleConfirmSignIn}
      />
    );
  }

  if (resetToken !== null) {
    return (
      <PasswordResetPage
        key={resetToken}
        token={resetToken}
        onChanged={handlePasswordChanged}
        onRequestNew={handleResetRequestNew}
      />
    );
  }

  if (session.status === "checking") {
    return checkingScreen;
  }

  if (session.status === "unavailable") {
    return (
      <CenterScreen>
        <div className="flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground">
            Демон недоступен. Проверьте соединение и повторите попытку.
          </p>
          <Button variant="outline" size="sm" onClick={retryCheck}>
            Повторить
          </Button>
        </div>
      </CenterScreen>
    );
  }

  if (session.status === "anonymous") {
    const openSignup = signupFlags.status === "ready" && signupFlags.options.open;
    if (forgotShown) {
      return <PasswordResetRequestScreen onBack={leaveSignupRoute} />;
    }
    // `#/confirm` без токена в памяти (перезагрузка, ручной ввод) — форма входа.
    if (signupRoute.active) {
      const gate = signupGate(signupFlags, signupRoute.token);
      if (gate.kind === "checking") {
        return (
          <AuthLayout busy>
            <AuthStatus>Проверяем регистрацию…</AuthStatus>
          </AuthLayout>
        );
      }
      if (gate.kind === "form") {
        return (
          <SignupForm
            token={signupRoute.token}
            turnstileSiteKey={
              signupFlags.status === "ready" ? signupFlags.options.turnstile_site_key : null
            }
            onBack={handleSignupBack}
          />
        );
      }
      return (
        <LoginForm
          onSuccess={handleLoginSuccess}
          notice={gate.notice ?? loginNotice}
          signupOpen={openSignup}
        />
      );
    }
    return (
      <LoginForm onSuccess={handleLoginSuccess} notice={loginNotice} signupOpen={openSignup} />
    );
  }

  if (forgotShown) {
    return <PasswordResetRequestScreen onBack={leaveSignupRoute} backLabel={forgotBackLabel} />;
  }

  // Поток забрала другая вкладка (§131 п. 3): экран целиком вместо каркаса, как
  // форма входа; переподключения нет, пока человек не нажмёт «Открыть здесь».
  if (connectionStatus === "taken_over") {
    return <TakenOverScreen onOpenHere={openHere} />;
  }

  // Настройки читаются до первого показа экрана; ключ — логин: смена
  // пользователя пересоздаёт провайдер, и чужие настройки не переживают её.
  return (
    <AccountSettingsProvider
      key={session.login}
      login={session.login}
      connectionStatus={connectionStatus}
      onSessionInvalid={handleSessionInvalid}
      fallback={checkingScreen}
    >
      <AppShell
        login={session.login}
        // Пункт «Пригласить»: право пользователя И флаг `signup-invite` у демона
        // (spec `web-navigation`).
        canInvite={
          session.canInvite && signupFlags.status === "ready" && signupFlags.options.invite
        }
        isOwner={session.isOwner}
        screen={screen}
        themeChoice={theme.choice}
        onThemeChoose={theme.choose}
        onLogout={handleLogout}
        onAccountDeleted={handleAccountDeleted}
        onAccountDeleteStart={deletionGuard.begin}
        onAccountDeleteFailed={deletionGuard.settleFailed}
        onSessionInvalid={handleSessionInvalid}
        streamStore={streamStore}
        connectionStatus={connectionStatus}
        connectionSinceMs={statusSinceMs}
        alerts={alerts}
        exchanges={alertExchanges}
        access={session.access}
        plans={session.plans}
      />
    </AccountSettingsProvider>
  );
}
